import { KeyboardEvent, useState } from "react";

import ChatSourceBadge from "@/components/chat/ChatSourceBadge";
import { submitFeedback, type SourceItem } from "@/services/aiService";
import { cn } from "@/utils/cn";

interface Props {
  sessionId: string;
  traceId: string | null;
  content: string;
  sources?: SourceItem[];
  onExpandSources?: (sources: SourceItem[]) => void;
}

export default function ChatFeedback({
  sessionId,
  traceId,
  content,
  sources,
  onExpandSources,
}: Props) {
  const [feedbackState, setFeedbackState] = useState<"up" | "down" | null>(
    null
  );
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSubmitted, setCommentSubmitted] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFeedback = async (value: "up" | "down") => {
    if (!traceId || isSubmitting) return;

    if (feedbackState === value) {
      setFeedbackState(null);
      setComment("");
      setCommentSubmitted(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitFeedback({
        session_id: sessionId,
        trace_id: traceId,
        value: value === "up" ? 1 : 0,
      });
      setFeedbackState(value);
      setComment("");
      setCommentSubmitted(false);
    } catch {
      // Keep previous state on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!traceId || !feedbackState || !comment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        session_id: sessionId,
        trace_id: traceId,
        value: feedbackState === "up" ? 1 : 0,
        comment: comment.trim(),
      });
      setCommentSubmitted(true);
    } catch {
      // Keep form visible on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  return (
    <div className="flex flex-col gap-[var(--chat-space-03)]">
      <div className="flex items-center gap-[var(--chat-space-06)] pt-[var(--chat-space-04)]">
        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="text-[var(--chat-grey-500)] hover:text-[var(--chat-black)] transition-colors"
          aria-label={copied ? "Copied" : "Copy message"}
          title={copied ? "Copied!" : "Copy"}
        >
          {copied ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </button>

        {/* Thumbs up */}
        <button
          onClick={() => handleFeedback("up")}
          disabled={isSubmitting}
          className={cn(
            "transition-colors",
            feedbackState === "up"
              ? "text-[var(--chat-black)]"
              : "text-[var(--chat-grey-500)] hover:text-[var(--chat-black)]"
          )}
          aria-label="Thumbs up"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={feedbackState === "up" ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
          </svg>
        </button>

        {/* Thumbs down */}
        <button
          onClick={() => handleFeedback("down")}
          disabled={isSubmitting}
          className={cn(
            "transition-colors",
            feedbackState === "down"
              ? "text-[var(--chat-black)]"
              : "text-[var(--chat-grey-500)] hover:text-[var(--chat-black)]"
          )}
          aria-label="Thumbs down"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={feedbackState === "down" ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
          </svg>
        </button>

        {/* Source badge */}
        {sources && sources.length > 0 && onExpandSources && (
          <ChatSourceBadge
            sources={sources}
            onExpand={() => onExpandSources(sources)}
          />
        )}
      </div>

      {/* Comment input — shown after rating, hidden after comment submitted */}
      {feedbackState && !commentSubmitted && (
        <div className="w-full mt-[var(--chat-space-03)]">
          <div className="flex items-center gap-[var(--chat-space-03)] w-full bg-[var(--chat-creme)] border border-[var(--chat-grey-100)] rounded-[var(--chat-corner-base)] px-[var(--chat-space-05)] py-[var(--chat-space-05)] min-h-[3.4rem]">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={handleCommentKeyDown}
              rows={1}
              placeholder={
                feedbackState === "up"
                  ? "What was helpful?"
                  : "What could be improved?"
              }
              className="flex-1 text-[var(--chat-text-base)] font-medium bg-transparent focus:outline-none focus:ring-0 border-none text-[var(--chat-black)] placeholder:text-[var(--chat-grey-500)] resize-none leading-[1.5] min-h-0"
            />
            <button
              onClick={handleSubmitComment}
              disabled={isSubmitting || !comment.trim()}
              className="shrink-0 w-[var(--chat-icon-base)] h-[var(--chat-icon-base)] text-[var(--chat-black)] disabled:opacity-30"
              aria-label="Send feedback"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Confirmation after comment sent */}
      {commentSubmitted && (
        <p className="text-[var(--chat-text-xs)] text-[var(--chat-grey-500)] mt-[var(--chat-space-02)]">
          Thanks for your feedback!
        </p>
      )}
    </div>
  );
}
